import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { getAccessToken, setAccessToken } from '../utils/token';

// Create the UserContext
export const UserContext = createContext(null);

// Create the provider component
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user identity from API
  const fetchIdentity = useCallback(async () => {
    try {
      let token = getAccessToken();
      if (!token) {
        // Try to refresh to get a new access token from cookie
        try {
          const { data: refresh } = await api.post('/users/token/refresh', null, { withCredentials: true });
          if (refresh && refresh.access_token) {
            setAccessToken(refresh.access_token);
            token = refresh.access_token;
          }
        } catch (_) {
          // no valid refresh cookie
        }
      }
      if (!token) {
        setLoading(false);
        return;
      }
      const { data } = await api.get('/users/me');
      setUser(data.data);
    } catch (err) {
      console.error('Failed to fetch current user:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh user data (call after settings changes, etc.)
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/users/me');
      setUser(data.data);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  }, []);

  useEffect(() => {
    // Avoid noisy identity calls on the login screen before any token exists
    if (typeof window !== 'undefined' && window.location && window.location.pathname === '/login') {
      setLoading(false);
      return;
    }
    fetchIdentity();
  }, [fetchIdentity]);

  return (
    <UserContext.Provider value={{ user, setUser, loading, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
};

/**
 * Custom hook to access the UserContext.
 * Must be used within a <UserProvider>.
 */
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
